import { useCallback, useEffect, useRef, useState } from 'react';
import { cooperplaceApi } from 'services/api';
import { assertAppliedFilters } from './Filters/contract';
import {
  GROUPS,
  PER_PAGE,
  validateResponse,
  assertUniqueItems,
  KanbanContractError,
} from './contract';

/** Seções do filtro que restringem cada coluna: Cargas (e candidatos) ou Viagens. */
export const GROUP_SECTIONS = {
  AVAILABLE: ['loads', 'candidates'],
  ATTENDED: ['travels'],
  IN_TRANSIT: ['travels'],
  AT_DESTINATION: ['travels'],
};
const CODES = GROUPS.map(({ code }) => code);

/** Colunas cujas seções do filtro mudaram entre duas versões dos filtros. */
export function changedGroups(previous, next) {
  const section = (filters, key) => JSON.stringify(filters?.[key] ?? null);
  return CODES.filter((code) =>
    GROUP_SECTIONS[code].some((key) => section(previous, key) !== section(next, key)),
  );
}

/** Identidade da consulta de cada coluna: empresa, seções do filtro e página. */
function groupKeys(companyId, filtersKey, pages) {
  let filters = filtersKey;
  try {
    filters = filtersKey ? JSON.parse(filtersKey) : null;
  } catch {
    // Texto inválido continua identificando a consulta; a API recusa o filtro.
  }
  return Object.fromEntries(
    CODES.map((code) => [
      code,
      JSON.stringify([
        companyId,
        GROUP_SECTIONS[code].map((key) =>
          filters && typeof filters === 'object' ? (filters[key] ?? null) : filters,
        ),
        pages[code],
      ]),
    ]),
  );
}

const messageFor = (error) => {
  const status = error.response?.status;
  return [401, 403].includes(status)
    ? 'Você não tem permissão para consultar o Kanban.'
    : status === 503
      ? 'Não foi possível concluir a avaliação dos candidatos. Refine os filtros ou tente novamente.'
      : [409, 422].includes(status)
        ? 'A consulta mudou ou os filtros não puderam ser aplicados. Atualize ou revise os filtros.'
        : error.response
          ? 'Não foi possível consultar o Kanban. Tente novamente.'
          : error instanceof KanbanContractError
            ? error.message
            : 'Não foi possível conectar ao Kanban. Tente novamente.';
};

/**
 * Consulta as colunas do Kanban. Ao aplicar filtros ou trocar a página, só as colunas cuja
 * consulta mudou mostram o carregamento e são consultadas de novo; as demais mantêm os cards.
 * Atualizar (refresh) e a troca de empresa recarregam as quatro.
 */
export default function useKanban({ companyId, enabled, pages, filtersKey = null }) {
  const [state, setState] = useState({
    keys: {},
    groups: {},
    loading: [],
    error: null,
    revision: null,
  });
  const latest = useRef(state);
  latest.current = state;
  const [revision, setRevision] = useState(0);
  const generation = useRef(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const pagesKey = CODES.map((code) => pages[code]).join(',');
  const keys = groupKeys(companyId, filtersKey, pages);
  const keysKey = JSON.stringify(keys);
  useEffect(() => {
    const current = ++generation.current;
    const abort = new AbortController();
    const nextKeys = JSON.parse(keysKey);
    const previous = latest.current;
    if (!enabled) {
      setState({ keys: nextKeys, groups: {}, loading: [], error: null, revision });
      return () => abort.abort();
    }
    const refreshed = previous.revision !== revision;
    const stale = CODES.filter(
      (code) =>
        refreshed ||
        previous.error ||
        previous.keys[code] !== nextKeys[code] ||
        !previous.groups[code],
    );
    // Colunas preservadas só quando a consulta delas é a mesma.
    const kept = Object.fromEntries(
      CODES.filter((code) => !stale.includes(code)).map((code) => [code, previous.groups[code]]),
    );
    // Atualizar a mesma consulta mantém os cards visíveis até a nova resposta.
    const showing = Object.fromEntries(
      CODES.filter((code) => previous.keys[code] === nextKeys[code] && previous.groups[code]).map(
        (code) => [code, previous.groups[code]],
      ),
    );
    setState({ keys: nextKeys, groups: showing, loading: stale, error: null, revision });
    if (!stale.length) return () => abort.abort();
    const pageNumbers = pagesKey.split(',').map(Number);
    const samePage = pageNumbers.every((page) => page === pageNumbers[0]);
    const queries =
      stale.length === CODES.length && samePage
        ? [{ page: pageNumbers[0], perPage: PER_PAGE }]
        : stale.map((code) => ({
            group: code,
            page: pageNumbers[CODES.indexOf(code)],
            perPage: PER_PAGE,
          }));
    Promise.all(
      queries.map(async (params) => {
        if (filtersKey) params.filters = filtersKey;
        const { data } = await cooperplaceApi.get('control-tower/kanban', {
          params,
          signal: abort.signal,
        });
        assertAppliedFilters(data, filtersKey);
        return validateResponse(data, params);
      }),
    )
      .then((replies) => {
        if (current !== generation.current || abort.signal.aborted) return;
        const fresh = Object.fromEntries(
          replies.flatMap((reply) =>
            reply.groups.map((group) => [group.code, { ...group, asOf: reply.asOf }]),
          ),
        );
        // Só as colunas desta consulta precisam ser coerentes entre si; as mantidas vêm de antes.
        assertUniqueItems(Object.values(fresh));
        const groups = { ...kept, ...fresh };
        setState({ keys: nextKeys, groups, loading: [], error: null, revision });
      })
      .catch((error) => {
        if (current !== generation.current || abort.signal.aborted) return;
        const status = error.response?.status;
        const preservePrevious =
          ![401, 403, 409, 422].includes(status) && !(error instanceof KanbanContractError);
        // Falha transitória ao repetir a mesma consulta conserva os cards anteriores.
        const groups = preservePrevious
          ? Object.fromEntries(
              CODES.filter(
                (code) =>
                  kept[code] || (previous.keys[code] === nextKeys[code] && previous.groups[code]),
              ).map((code) => [code, kept[code] || previous.groups[code]]),
            )
          : {};
        setState({ keys: nextKeys, groups, loading: [], error: messageFor(error), revision });
      });
    return () => abort.abort();
  }, [enabled, keysKey, pagesKey, revision, filtersKey]);
  // Nunca apresenta dados de outra consulta: compara a identidade de cada coluna no render.
  const current = (code) => state.keys[code] === keys[code];
  const loadingGroups = Object.fromEntries(
    CODES.map((code) => [code, enabled && (!current(code) || state.loading.includes(code))]),
  );
  const groups = Object.fromEntries(
    CODES.filter((code) => current(code) && state.groups[code]).map((code) => [
      code,
      state.groups[code],
    ]),
  );
  return {
    groups: Object.keys(groups).length ? groups : null,
    loading: CODES.some((code) => loadingGroups[code]),
    loadingGroups,
    error: CODES.every(current) ? state.error : null,
    refresh,
  };
}
